import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Utils
import DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS from 'organisms/DeliveryAddressSearchForm/Constants/DeliveryAddressSearchFormConstants';
import { DISPOSITION_KEYS } from 'common/constants/SharedConstants';
import START_ORDER_CONSTANTS from '../../StartOrderComponent/Constants/startOrderConstants';
import { resetStores } from '@kfc-global/react-shared/redux/Actions/StoresAction';
import { getFooterLinkIcon, getValueOrDefaultObject } from 'common/utilities/utils';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import {
  getDeliveryAddressSearchTypes,
  getDeliveryAddressTextFields,
  selectedAddressDefault,
} from '../DeliveryAddressSearchFormUtilities/DeliveryAddressSearchFormUtils';
import Strings from '@kfc-global/react-shared/constants/Strings';
import { setLocalStorage } from '../../SearchStore/AU/Presentation/DispositionModuleUtils/DispositionModuleUtils';
import { getDeliveryAggregatorContent } from 'common/utilities/content';
import isEmpty from 'lodash/isEmpty';

// Components
import { SignInSection } from '../Presentation/SignInSection';
import { UserOrderContext } from '../../../context/context';
import SearchErrors from 'organisms/SearchStore/AU/Presentation/SearchErrors';
import ToggleTabsComponent from 'molecules/ToggleTabsComponent/ToggleTabsComponent';
import SearchableDropdownComponent from 'molecules/SearchableDropdownComponent/SearchableDropdownComponent';
import PostalCodeFormComponent from 'molecules/PostalCodeFormComponent/PostalCodeFormComponent';
import { MaterialTextInput } from 'atoms/MaterialInput';
import ImageComponent from 'atoms/Image';
import { ContainedButton } from 'atoms/Buttons';
import SavedAddressView from '../../SearchStore/AU/Presentation/SavedAddressView';

const { DISPOSITION_METHOD } = START_ORDER_CONSTANTS;
const {
  CHOME,
  CONFIRM_LOCATION,
  NO_STORE_WITH_DELIVERY,
  OTHER_DELIVERY_SERVICES,
  SEARCH_DELIVERY_LOCATION,
  STORES_VARY_MESSAGE,
  SWITCH_TO_TAKE_AWAY,
  POSTAL_CODES,
  PREFECTURES,
} = DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS;

/**
 * DeliveryAddressSearchForm - Search form used to find JP delivery address
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const DeliveryAddressSearchForm = props => {
  const {
    deliveryLocalizationData,
    formContent,
    getStoreData,
    deliverySearchType,
    onDeliveryTypeChanged,
    isDeliveryLocalizationLoading,
    storesStatusError,
    saveAddressData,
    selectedAddress,
    setSelectedAddress,
  } = props;
  const storeSelection = {
    signInLink: true,
    useMyLocation: false,
  };

  const userParams = useSelector(data => data.appStateReducer.userStatus);
  const { postalCodePrefix } = useSelector(data => data.tenantReducer || {});
  const { headerData: { footer = {} } = {} } = useSelector(state => state?.marketConfigReducer?.response || {});
  const externalLinkIconUrl = getFooterLinkIcon(footer);
  const { loggedIn } = getValueOrDefaultObject(userParams);
  const { userOrderState, userOrderStateDispatch } = useContext(UserOrderContext);
  const dispatch = useDispatch();
  const [isChosenSavedAddress, setIsChosenSavedAddress] = useState(false);
  const [latestAddressStep, setLatestAddressStep] = useState(0);
  const [isPostalCodeVerified, setIsPostalCodeVerified] = useState(false);
  const [isOptionalFieldsVisible, setIsOptionalFieldsVisible] = useState(false);
  const [currentAddressStep, setCurrentAddressStep] = useState(0);
  const deliveryAddressSearchTypes = getDeliveryAddressSearchTypes();
  const deliverySearchTypeId = deliverySearchType?.id;
  const defaultSelectedTabIndex = Math.max(
    0,
    deliveryAddressSearchTypes.findIndex(type => type.id === deliverySearchTypeId),
  );
  const [isConfirmLocationEnabled, setIsConfirmLocationEnabled] = useState(false);
  const addressTextFields = getDeliveryAddressTextFields();
  const [isDeliverable, setIsDeliverable] = useState(true);
  const [filteredData, setFilteredData] = useState({});
  const isDeliveryAggregatorsLoading = useSelector(state => state?.deliveryAggregatorReducer?.isLoading || false);
  const deliveryAggregators = useSelector(
    state => state?.deliveryAggregatorReducer?.response?.deliveryAggregators || {},
  );
  const { savedAddresses } = useSelector(data => data?.profileReducer);
  const showSavedAddresses = !!(savedAddresses?.length && loggedIn);
  const selectedSavedAddress = useSelector(
    state => state?.pwaStoragsReducer?.pwaStorageState?.selectedFavouriteStoreAddress,
  );

  const deliveryUnavailableErrorMessage = {
    text: NO_STORE_WITH_DELIVERY,
    cta: {
      label: SWITCH_TO_TAKE_AWAY,
    },
  };
  const thirdPartyDeliveryServices = {
    header: OTHER_DELIVERY_SERVICES,
    notice: STORES_VARY_MESSAGE,
  };

  /**
   * show additional fields after the initial fields are completed
   */
  useEffect(() => {
    if (currentAddressStep === deliverySearchType?.fieldOrder?.length) {
      setIsOptionalFieldsVisible(true);
    }
  }, [deliverySearchType, isOptionalFieldsVisible, currentAddressStep]);

  useEffect(() => {
    if (isEmpty(deliveryAggregators) && !isDeliveryAggregatorsLoading) {
      dispatch(getDeliveryAggregatorContent());
    }
  }, [deliveryAggregators, dispatch, isDeliveryAggregatorsLoading]);

  /**
   * show fields once click edit changes on the Order Mode
   */
  useEffect(() => {
    const selectedAddressData = userOrderState?.scheduleOrderData?.selectedAddress;
    if (selectedAddressData && Object.keys(selectedAddressData).length > 0) {
      setSelectedAddress(selectedAddressData);
      setCurrentAddressStep(deliverySearchType?.fieldOrder?.length);
      setLatestAddressStep(deliverySearchType?.fieldOrder?.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * check if all required fields have values in order to enable the confirm cta
   */
  useEffect(() => {
    if (deliverySearchTypeId === Strings.address) {
      setIsConfirmLocationEnabled(
        !!(
          isDeliverable &&
          selectedAddress.prefecture &&
          selectedAddress.cityTown &&
          selectedAddress.chome &&
          selectedAddress.streetDetailAddress
        ),
      );
    }
    if (deliverySearchTypeId === Strings.postalCode) {
      setIsConfirmLocationEnabled(
        !!(isDeliverable && selectedAddress.postalCode && selectedAddress.chome && selectedAddress.streetDetailAddress),
      );
    }
  }, [
    deliverySearchTypeId,
    isDeliverable,
    selectedAddress.postalCode,
    selectedAddress.prefecture,
    selectedAddress.cityTown,
    selectedAddress.chome,
    selectedAddress.streetDetailAddress,
    setIsConfirmLocationEnabled,
  ]);

  const onSwitchToPickup = () => {
    userOrderStateDispatch({
      type: DISPOSITION_METHOD,
      value: { dispositionType: DISPOSITION_KEYS.PICKUP },
    });
    dispatch(resetStores());
  };

  /**
   * Add the redux data to the filtered data state
   */
  useEffect(() => {
    const keyObj = deliverySearchTypeId === Strings.postalCode ? POSTAL_CODES : PREFECTURES;
    const localisationData = deliveryLocalizationData[keyObj];
    if (!isDeliveryLocalizationLoading && !isEmpty(localisationData) && Object.keys(localisationData).length > 0) {
      const selectedField = deliverySearchTypeId === Strings.postalCode ? Strings.postalCode : Strings.prefecture;
      //If there is state date saved use that otherwise use the initial network data
      const storeId = userOrderState?.scheduleOrderData?.storeId;
      const localisationDataFiltered = {
        [selectedField]: localisationData,
      };
      let newFilteredData = {};
      if (selectedAddress?.[selectedField] && storeId) {
        //Set the prev. data when the user comes back
        newFilteredData = {
          ...localisationDataFiltered,
        };
        const fields = deliverySearchType?.fieldOrder || [];
        for (let i = 1; i < fields.length + 1; i++) {
          const currentFieldKey = fields[i]?.id || Strings.selectedStore;
          const prevFieldKey = fields[i - 1]?.id;
          const prevFieldValue = selectedAddress[prevFieldKey];
          const filteredPrevFieldValue = prevFieldValue.replace('-', '');
          newFilteredData[currentFieldKey] = newFilteredData[prevFieldKey][filteredPrevFieldValue];
        }
      }
      setFilteredData({
        ...newFilteredData,
        ...filteredData,
        ...localisationDataFiltered,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryLocalizationData]);

  /**
   * confirmAndSaveLocation - Called on confirm click, makes the google places api calls to get and format delivery address
   * @type {useCallback}
   */
  const confirmAndSaveLocation = useCallback(async () => {
    dispatch(resetStores());

    let addressLine = '';
    if (deliverySearchTypeId === Strings.postalCode) {
      addressLine += `${postalCodePrefix}${selectedAddress.postalCode}`;
    }
    if (deliverySearchTypeId === Strings.address) {
      addressLine += `${selectedAddress.prefecture}${selectedAddress.cityTown}`;
    }
    addressLine += `${selectedAddress.chome}${selectedAddress.streetDetailAddress}`;

    const additionalAddressInfo = `${selectedAddress.apartmentName} ${selectedAddress.roomNumber} ${selectedAddress.companyName}`;
    setLocalStorage(addressLine);

    const selectedStore = filteredData[Strings.selectedStore];
    if (selectedStore) {
      saveAddressData(addressLine, additionalAddressInfo, selectedStore);
      getStoreData(filteredData[Strings.selectedStore]); // pass store id;
    } else {
      setIsDeliverable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, selectedAddress]);

  /**
   * @function savedAddressSelectHandler
   * @description Takes saved address object as a value and stores it as formatted selected address object
   * @param value {object} User's stored saved delivery address
   */
  const savedAddressSelectHandler = value => {
    const savedAddress = selectedSavedAddress || value;
    if (savedAddress) {
      const postalCode = savedAddress?.pinCode || '';
      const prefecture = savedAddress?.addressLines?.[0] || '';
      const cityTown = savedAddress?.addressLines?.[1] || '';
      const chome = savedAddress?.addressLines?.[2] || '';
      const streetDetailAddress = savedAddress?.addressLines?.[3] || '';
      const apartmentName = savedAddress?.addressLines?.[4] || '';
      const roomNumber = savedAddress?.addressLines?.[5] || '';
      const companyName = savedAddress?.addressLines?.[6] || '';
      const specialNote = savedAddress?.addressLines?.[7] || '';

      setSelectedAddress({
        ...selectedAddress,
        apartmentName,
        chome,
        cityTown,
        companyName,
        postalCode,
        prefecture,
        roomNumber,
        streetDetailAddress,
        specialNote,
      });
      let getSelectedStoreData = [];
      if (postalCode) {
        getSelectedStoreData = filteredData?.[Strings.postalCode]?.[postalCode]?.[chome];
      } else if (prefecture) {
        getSelectedStoreData = filteredData?.[Strings.prefecture]?.[prefecture]?.[cityTown]?.[chome];
      }
      setFilteredData({
        ...filteredData,
        [Strings.selectedStore]: getSelectedStoreData,
      });
      setIsChosenSavedAddress(true);
    }
  };

  useEffect(() => {
    if (isChosenSavedAddress) {
      confirmAndSaveLocation();
    }
  }, [confirmAndSaveLocation, isChosenSavedAddress]);

  useEffect(() => {
    setIsDeliverable(!storesStatusError);
  }, [storesStatusError]);

  /**
   * changeDeliveryAddressSearchType - callback for when delivery search type tab is selected
   * @param index {Boolean} - index in delivery address search types array that was selected
   */
  const changeDeliveryAddressSearchType = index => {
    const newSearchType = deliveryAddressSearchTypes[index];
    onDeliveryTypeChanged(newSearchType);
    setCurrentAddressStep(0);
    setIsPostalCodeVerified(Boolean(selectedAddress[Strings.postalCode]));
    setIsOptionalFieldsVisible(false);
    setIsDeliverable(true);
    if (selectedAddress[newSearchType.fieldOrder[0].id]) {
      setCurrentAddressStep(latestAddressStep);
    }
  };

  /**
   * @function clearAddressData - clear the data of previous tab,
   * when you have saved at least one field in the current tab
   * @param {Boolean} nextStep - index of the next form step
   * @returns previous tab data should be clear or not
   */
  const clearAddressData = nextStep => {
    if (nextStep <= 1 && deliverySearchTypeId === Strings.address) {
      return Boolean(selectedAddress[Strings.postalCode]);
    } else if (nextStep <= 1 && deliverySearchTypeId === Strings.postalCode) {
      return Boolean(selectedAddress[Strings.prefecture]);
    } else {
      return false;
    }
  };

  /**
   * saveSelectionAndEnableStep - saves the selection from the current field and determines the next to enable
   * @param selection {String} - the selected text from current field
   * @param fieldId {String} - id used to determine the data type for the field
   * @param nextStep {Number} - index of next step to determine which field to display next
   */
  const saveSelectionAndEnableStep = (selection, fieldId, nextStep) => {
    const newSelectedAddressDetails = clearAddressData(nextStep)
      ? { ...selectedAddressDefault }
      : { ...selectedAddress };
    newSelectedAddressDetails[fieldId] = selection;
    setCurrentAddressStep(nextStep);
    setLatestAddressStep(nextStep);
    if (nextStep < deliverySearchType.fieldOrder.length) {
      for (let i = nextStep; i < deliverySearchType.fieldOrder.length; i++) {
        const otherField = deliverySearchType.fieldOrder[i];
        newSelectedAddressDetails[otherField.id] = '';
      }
    }
    setSelectedAddress({
      ...selectedAddress,
      ...newSelectedAddressDetails,
    });
    setIsDeliverable(true); // Clear prev. error

    if (selection) {
      const nextFieldId = deliverySearchType.fieldOrder[nextStep]?.id || Strings.selectedStore;
      const filteredSelection = selection.replace('-', '');
      const nextStepData = filteredData[fieldId][filteredSelection];
      setFilteredData({
        ...filteredData,
        [nextFieldId]: nextStepData,
      });
      if (!nextStepData) {
        // Show the error scenario
        setIsDeliverable(false);
      }
    }
  };

  /**
   * saveTextFieldAddressValue - gets new value for a specific address field and saves it to our data
   * @param event
   * @param fieldId {String} - id used to determine the data type for the field
   */
  const saveTextFieldAddressValue = (event, fieldId) => {
    const newTextValue = event.target.value;
    const newTextFieldAddressDetails = { ...selectedAddress };
    newTextFieldAddressDetails[fieldId] = newTextValue;

    setSelectedAddress({
      ...selectedAddress,
      ...newTextFieldAddressDetails,
    });
  };

  /**
   * renderAddressTextFields - renders all address text fields that are required
   * @returns {JSX.Element}
   */
  const renderAddressTextFields = () => {
    return (
      <div className='text-field-container'>
        {addressTextFields.map(function (textFieldDetail) {
          return (
            <MaterialTextInput
              key={textFieldDetail.id}
              className='address-text-field'
              labelClassName='text-field-title'
              containerClassName='text-field-content'
              name={textFieldDetail.id}
              placeholder={translateWithI18Next(textFieldDetail.title) || textFieldDetail.title}
              value={selectedAddress[textFieldDetail.id]}
              onChange={event => saveTextFieldAddressValue(event, textFieldDetail.id)}
            />
          );
        })}
      </div>
    );
  };

  /**
   * renderDeliveryAddressForm - renders the delivery address search form fields for the address tab
   * @returns {JSX.Element}
   */
  const renderDeliveryAddressForm = () => {
    const stepToValidate = currentAddressStep + (isOptionalFieldsVisible ? 1 : 0);
    return (
      <div className='address-search-form'>
        {deliverySearchType.fieldOrder.map(function (fieldDetails, index) {
          if (index <= stepToValidate) {
            const fieldId = deliverySearchType.fieldOrder[index]?.id;
            const fieldItems = filteredData[fieldId];
            if (!fieldItems) {
              return null;
            }
            const dropdownList = Object.keys(fieldItems);
            return (
              <SearchableDropdownComponent
                dropdownList={fieldId === CHOME ? dropdownList.sort() : dropdownList}
                dropdownTitleText={translateWithI18Next(fieldDetails.title) || fieldDetails.title}
                enableSearch
                key={fieldDetails.id}
                onDropdownSelection={selection => saveSelectionAndEnableStep(selection, fieldDetails.id, index + 1)}
                selectedText={selectedAddress?.[fieldDetails.id] || translateWithI18Next(fieldDetails.title)}
              />
            );
          }
          return null;
        })}
        {isOptionalFieldsVisible && renderAddressTextFields()}
      </div>
    );
  };

  /**
   * verifyPostalCode - it's a callback which is called from PostalCodeFormComponent
   * clear the chome when the postalCode changes and save the values
   * @param isPostalCodeVerifiedCheck {Boolean} - to check the size of postal code value
   * @param postalCode {String} - the postalCode final to be saved
   */
  const verifyPostalCode = (isPostalCodeVerifiedCheck, postalCode) => {
    setIsPostalCodeVerified(isPostalCodeVerifiedCheck);
    if (isPostalCodeVerifiedCheck) {
      saveSelectionAndEnableStep(postalCode, Strings.postalCode, 1);
    } else {
      saveSelectionAndEnableStep('', Strings.chome, 0);
    }
  };

  /**
   * renderPostalCodeForm - renders the form which is used to search a postal code
   * @returns {JSX.Element}
   */
  const renderPostalCodeForm = () => {
    const chomeData = Object.keys(filteredData[Strings.chome] || []).sort();
    const hasChomes = chomeData.length > 0;
    return (
      <>
        <PostalCodeFormComponent
          defaultPostalValue={selectedAddress?.[Strings.postalCode]}
          verifyPostalCode={verifyPostalCode}
        />
        {hasChomes && (isPostalCodeVerified || isOptionalFieldsVisible) && (
          <div className='address-search-form'>
            <SearchableDropdownComponent
              dropdownList={chomeData}
              dropdownTitleText={
                translateWithI18Next(deliverySearchType.fieldOrder[1].title) || deliverySearchType.fieldOrder[1].title
              }
              enableSearch
              key={deliverySearchType.fieldOrder[1].id}
              onDropdownSelection={selection =>
                saveSelectionAndEnableStep(selection, deliverySearchType.fieldOrder[1].id, 2)
              }
              selectedText={
                selectedAddress?.[deliverySearchType.fieldOrder[1].id] ||
                translateWithI18Next(deliverySearchType.fieldOrder[1].title)
              }
            />
          </div>
        )}
        {hasChomes && isOptionalFieldsVisible && renderAddressTextFields()}
      </>
    );
  };

  const undeliverableAddressRef = useCallback(errorMessage => {
    errorMessage?.scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  return (
    <div className='delivery-address-form delivery-module-form' data-testid='delivery-container'>
      <div className='delivery-address-form-header'>
        <SignInSection loggedIn={loggedIn} formContent={formContent} storeSelection={storeSelection} />
        <p className='delivery-address-form-search-text'>
          {translateWithI18Next(SEARCH_DELIVERY_LOCATION) || SEARCH_DELIVERY_LOCATION}
        </p>
        <ToggleTabsComponent
          onTabSelection={changeDeliveryAddressSearchType}
          tabsList={deliveryAddressSearchTypes}
          defaultSelectedTabIndex={defaultSelectedTabIndex}
        />
      </div>
      {deliverySearchTypeId === Strings.address ? renderDeliveryAddressForm() : renderPostalCodeForm()}
      <ContainedButton
        className={`delivery-address-form-confirm-cta ${isConfirmLocationEnabled ? '' : 'disabled'}`}
        disabled={!isConfirmLocationEnabled}
        onClick={confirmAndSaveLocation}
        variant='secondary'
        data-testid='delivery-address-form-confirm-cta'
      >
        <span>{translateWithI18Next(CONFIRM_LOCATION) || CONFIRM_LOCATION}</span>
      </ContainedButton>
      {isDeliverable ? (
        ''
      ) : (
        <div className='undeliverable-address' ref={undeliverableAddressRef}>
          <SearchErrors
            show={true}
            errorObj={{
              showError: true,
              errMsg: translateWithI18Next(deliveryUnavailableErrorMessage.text),
              errDesc: null,
              firstBtnText: translateWithI18Next(deliveryUnavailableErrorMessage.cta.label),
              firstBtnCTA: onSwitchToPickup,
            }}
          />
          {!isEmpty(deliveryAggregators) && (
            <div className='third-party-delivery-services'>
              <p className='delivery-address-form-search-text'>
                {translateWithI18Next(thirdPartyDeliveryServices.header)}
              </p>
              <div className='third-party-delivery-services__list'>
                {deliveryAggregators?.items?.map(aggregator => {
                  const aggregatorFieldData = aggregator?.fields;
                  return (
                    <a
                      key={aggregatorFieldData?.name}
                      href={aggregatorFieldData?.link}
                      target='_blank'
                      rel='noreferrer'
                      className='third-party-delivery-services__service'
                    >
                      <span className='third-party-delivery-services__image-container'>
                        <ImageComponent
                          srcFile={`${Strings.https}${aggregatorFieldData?.logo?.fields?.file?.url}`}
                          alt={aggregatorFieldData?.logo?.fields?.title}
                          className='third-party-delivery-services__images'
                        />
                      </span>
                      <span className='third-party-delivery-services__label'>{aggregatorFieldData?.name}</span>
                      <ImageComponent
                        srcFile={externalLinkIconUrl}
                        alt=''
                        className='third-party-delivery-services__image'
                      />
                    </a>
                  );
                })}
              </div>
              <p className='third-party-delivery-services__notice'>
                {translateWithI18Next(thirdPartyDeliveryServices.notice)}
              </p>
            </div>
          )}
        </div>
      )}
      {showSavedAddresses && (
        <div className='delivery-saved-addresses'>
          <div className='placesautocomplete-border'></div>
          <SavedAddressView
            show={showSavedAddresses}
            savedAddresses={savedAddresses}
            handleSelectAddress={savedAddressSelectHandler}
            shouldMaintainAddressObject={true}
          />
        </div>
      )}
    </div>
  );
};
export default DeliveryAddressSearchForm;
